import { useEffect, useRef, useState } from 'react'
import OLMap from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import TileWMS from 'ol/source/TileWMS'
import WMSCapabilities from 'ol/format/WMSCapabilities'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style, Circle as CircleStyle } from 'ol/style'
import 'ol/ol.css'
import { useMapStore } from '../../store/mapStore'
import { useGeometryStore } from '../../store/geometryStore'
import { useGeometry } from '../../hooks/useGeometry'
import { useSaveMapSettings } from '../../api/settings'
import { useToast } from '../../hooks/useToast'
import DrawingToolbar from './DrawingToolbar'
import CoordinateBar from './CoordinateBar'

// OL draw style constants
const DRAW_STYLE = new Style({
  fill: new Fill({ color: 'rgba(59,130,246,0.15)' }),
  stroke: new Stroke({ color: '#3b82f6', width: 2 }),
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: '#ffffff' }),
    stroke: new Stroke({ color: '#3b82f6', width: 2 }),
  }),
})

interface WmsLayerMetadata {
  name?: string
  title?: string
  layers?: WmsLayerMetadata[]
}

interface WmsCapabilities {
  Capability?: {
    Layer?: WmsLayerMetadata
  }
}

interface ActiveSourceConfig {
  id: string
  name: string
  endpoint: string
  layers: string[]
}

function buildCapabilitiesUrl(endpoint: string): string {
  const joiner = endpoint.includes('?') ? '&' : '?'
  return `${endpoint}${joiner}SERVICE=WMS&REQUEST=GetCapabilities&VERSION=1.3.0`
}

function collectLayerMetadata(layer: WmsLayerMetadata | undefined, out: WmsLayerMetadata[] = []): WmsLayerMetadata[] {
  if (!layer) return out
  out.push(layer)
  if (layer.layers?.length) {
    for (const child of layer.layers) {
      collectLayerMetadata(child, out)
    }
  }
  return out
}

async function resolveWmsLayerNames(endpoint: string, configuredLayers: string[]): Promise<string[]> {
  const response = await fetch(buildCapabilitiesUrl(endpoint))
  if (!response.ok) return configuredLayers

  const capabilitiesText = await response.text()
  const parsed = new WMSCapabilities().read(capabilitiesText) as WmsCapabilities
  const allLayers = collectLayerMetadata(parsed.Capability?.Layer)

  const names = new Set(
    allLayers
      .map((layer) => layer.name?.trim())
      .filter((name): name is string => Boolean(name))
  )

  const titleToName = new Map(
    allLayers
      .filter((layer) => layer.title?.trim() && layer.name?.trim())
      .map((layer) => [layer.title!.trim().toLowerCase(), layer.name!.trim()])
  )

  return configuredLayers.map((token) => {
    const trimmed = token.trim()
    if (!trimmed) return trimmed
    if (names.has(trimmed)) return trimmed
    return titleToName.get(trimmed.toLowerCase()) ?? trimmed
  })
}

export default function MapView() {
  const mapRef = useRef<HTMLDivElement>(null)
  const [olMap, setOlMap] = useState<OLMap | null>(null)
  const drawSourceRef = useRef(new VectorSource())
  const wmsLayerRef = useRef<TileLayer<TileWMS> | null>(null)

  const {
    center,
    zoom,
    wmsEndpoint,
    wmsLayers,
    wmsSources,
    activeWmsSourceId,
    setWmsEndpoint,
    setWmsLayers,
    setActiveWmsSourceId,
  } = useMapStore()
  const { isModalOpen } = useGeometryStore()
  const { mutate: saveMapSettings, isPending: isSavingMapSettings } = useSaveMapSettings()
  const { toast } = useToast()
  const activeSource: ActiveSourceConfig | null =
    wmsSources.find((source) => source.id === activeWmsSourceId) ?? wmsSources[0] ?? null
  const effectiveWmsEndpoint = activeSource?.endpoint ?? wmsEndpoint
  const effectiveWmsLayers = activeSource?.layers ?? wmsLayers

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return

    const drawLayer = new VectorLayer({
      source: drawSourceRef.current,
      style: DRAW_STYLE,
    })

    const map = new OLMap({
      target: mapRef.current,
      layers: [drawLayer],
      view: new View({
        projection: 'EPSG:4326',
        center: center as [number, number], // [lon, lat] - GeoJSON order
        zoom,
      }),
    })

    setOlMap(map)

    return () => {
      map.setTarget(undefined)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync persisted/default view to OL map when settings load or change.
  useEffect(() => {
    if (!olMap) return
    const view = olMap.getView()
    const currentCenter = view.getCenter()
    const currentZoom = view.getZoom()

    if (
      !currentCenter ||
      currentCenter[0] !== center[0] ||
      currentCenter[1] !== center[1]
    ) {
      view.setCenter(center as [number, number]) // [lon, lat] - GeoJSON order
    }

    if (typeof currentZoom !== 'number' || currentZoom !== zoom) {
      view.setZoom(zoom)
    }
  }, [olMap, center, zoom])

  // WMS layer management
  useEffect(() => {
    if (!olMap) return

    if (wmsLayerRef.current) {
      olMap.removeLayer(wmsLayerRef.current)
      wmsLayerRef.current = null
    }

    if (!effectiveWmsEndpoint || effectiveWmsLayers.length === 0) return

    let cancelled = false

    const addWmsLayer = async () => {
      let resolvedLayers = effectiveWmsLayers
      try {
        resolvedLayers = await resolveWmsLayerNames(effectiveWmsEndpoint, effectiveWmsLayers)
      } catch {
        // Fallback to configured layer values if capabilities fetch/parse fails.
      }

      if (cancelled) return

      const layer = new TileLayer({
        source: new TileWMS({
          url: effectiveWmsEndpoint,
          params: { LAYERS: resolvedLayers.join(','), TILED: true },
          serverType: 'geoserver',
        }),
        zIndex: 0,
      })

      olMap.getLayers().insertAt(0, layer)
      wmsLayerRef.current = layer
    }

    void addWmsLayer()

    return () => {
      cancelled = true
    }
  }, [olMap, effectiveWmsEndpoint, effectiveWmsLayers])

  useGeometry(olMap, drawSourceRef.current)

  function switchWmsSource(sourceId: string) {
    const source = wmsSources.find((item) => item.id === sourceId)
    if (!source) return

    setActiveWmsSourceId(source.id)
    setWmsEndpoint(source.endpoint)
    setWmsLayers(source.layers)
    toast({ title: `Switched WMS: ${source.name}` })

    saveMapSettings(
      {
        wmsSources,
        activeWmsSourceId: source.id,
        wmsEndpoint: source.endpoint,
        wmsLayers: source.layers,
      },
      {
        onError: () => {
          toast({ title: 'Failed to switch WMS source', variant: 'destructive' })
        },
      }
    )
  }

  function rotateWmsSource(direction: 1 | -1) {
    if (wmsSources.length < 2) return
    const currentIndex = Math.max(
      0,
      wmsSources.findIndex((source) => source.id === activeWmsSourceId)
    )
    const nextIndex = (currentIndex + direction + wmsSources.length) % wmsSources.length
    switchWmsSource(wmsSources[nextIndex].id)
  }

  function handleSaveHomeView() {
    if (!olMap) return
    const view = olMap.getView()
    const viewCenter = view.getCenter()
    const viewZoom = view.getZoom()

    if (!viewCenter || typeof viewZoom !== 'number') {
      toast({ title: 'Unable to read map view', variant: 'destructive' })
      return
    }

    const [lon, lat] = viewCenter as [number, number] // [lon, lat] - GeoJSON order
    const normalizedZoom = Math.max(1, Math.min(20, Math.round(viewZoom)))

    saveMapSettings(
      {
        defaultCenterLon: lon,
        defaultCenterLat: lat,
        defaultZoom: normalizedZoom,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Home view saved',
            description: `Lat ${lat.toFixed(5)}, Lon ${lon.toFixed(5)}, Zoom ${normalizedZoom}`,
          })
        },
        onError: () => {
          toast({ title: 'Failed to save home view', variant: 'destructive' })
        },
      }
    )
  }

  return (
    <div className="relative flex-1 h-full">
      <div ref={mapRef} className="absolute inset-0" />

      {!isModalOpen && (
        <>
          <DrawingToolbar />
          <div className="absolute top-4 right-4 z-10">
            <div className="w-72 space-y-2">
              {wmsSources.length > 0 && (
                <div className="rounded-md border border-white/20 bg-black/45 p-2 text-white/90 backdrop-blur-md shadow-lg">
                  <div className="mb-1 text-[11px] font-semibold tracking-wide uppercase text-white/70">
                    WMS Source
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => rotateWmsSource(-1)}
                      disabled={isSavingMapSettings || wmsSources.length < 2}
                      className="rounded border border-white/25 px-2 py-1 text-xs hover:bg-black/35 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Previous source"
                      type="button"
                    >
                      Prev
                    </button>
                    <select
                      value={activeWmsSourceId ?? ''}
                      onChange={(e) => switchWmsSource(e.target.value)}
                      disabled={isSavingMapSettings}
                      className="flex-1 rounded border border-white/25 bg-black/40 px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-white/40"
                      title="Select active WMS source"
                    >
                      {wmsSources.map((source) => (
                        <option key={source.id} value={source.id} className="bg-black text-white">
                          {source.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => rotateWmsSource(1)}
                      disabled={isSavingMapSettings || wmsSources.length < 2}
                      className="rounded border border-white/25 px-2 py-1 text-xs hover:bg-black/35 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Next source"
                      type="button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleSaveHomeView}
                disabled={isSavingMapSettings}
                className="w-full rounded-md border border-white/20 bg-black/45 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur-md shadow-lg hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Save current map location and zoom as your home view"
                type="button"
              >
                {isSavingMapSettings ? 'Saving...' : 'Save Home View'}
              </button>
            </div>
          </div>
          <CoordinateBar map={olMap} />
        </>
      )}
    </div>
  )
}
